import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/customer-ui/button'
import { Input } from '@/components/customer-ui/input'
import { Label } from '@/components/ui/label'

import { Info, Loader2, Plus, Save, Trash2, XIcon, Zap } from 'lucide-react'

import client from '@/http/customer-client'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/customer-ui/tooltip'
import { DatePicker } from '@modules/manifera-user/resources/js/pages/customer-app/components/date-picker.tsx'
import { DeleteConfirmDialog } from '@modules/manifera-user/resources/js/pages/customer-app/components/delete-confirm-dialog.tsx'
import {
  ApiChargingStation,
  ChargingStation
} from '@modules/manifera-user/resources/js/types'
import { emptyStation } from '@modules/manifera-user/resources/js/utils/helpers'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { ErrorBoundary } from '@suspensive/react'
import { toast } from 'react-toastify'
import { Skeleton } from '@/components/ui/skeleton'
import { ERE_FACTOR } from '@modules/manifera-user/resources/js/utils/station-calculation'
import { MIDsAutocomplete } from '@/components/MIDs-autocomplete'
import { Mid } from '../../../page-mid-overview/schema'
import { Customer } from '@modules/manifera-user/resources/js/schema'

const MAX_STATIONS = 3
const toVolumeInputValue = (value: number | null | undefined): string =>
  value == null ? '' : String(value)

const fetchStations = async (): Promise<ApiChargingStation[]> => {
  const res = await client.get<{ data: ApiChargingStation[] }>(
    '/app-api/me/stations'
  )

  return res.data.data
}

const ChargingStationsTab = ({ customer }: { customer?: Customer }) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [stations, setStations] = useState<ChargingStation[]>([])
  const [grandTotal, setGrandTotal] = useState<string>('')
  const [savingStationId, setSavingStationId] = useState<string | null>(null)
  const [isShowSaveButton, setIsShowSaveButton] = useState(false)
  const [popupMessage, setPopupMessage] = useState('')
  const [popupTitle, setPopupTitle] = useState<string | null>(null)
  const [formError, setFormError] = useState('')
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [isShowPopup, setIsShowPopup] = useState(false)
  const [missingBrandByStation, setMissingBrandByStation] = useState<
    Record<string, boolean>
  >({})
  const [missingEvseIdByStation, setMissingEvseIdByStation] = useState<
    Record<string, boolean>
  >({})
  const [tooltipOpenId, setTooltipOpenId] = useState<string | null>(null)
  const [scrollTarget, setScrollTarget] = useState<string | null>(null)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-stations'],
    queryFn: fetchStations
  })
  const chargingStationHelpItems = [
    {
      label: t('Charging stations help label charged kwh'),
      value: t('Charging stations help value charged kwh')
    },
    {
      label: t('Charging stations help label period'),
      value: t('Charging stations help value period')
    },
    {
      label: t('Charging stations help label proof'),
      value: t('Charging stations help value proof')
    },
    {
      label: t('Charging stations help label connection'),
      value: t('Charging stations help value connection')
    },
    {
      label: t('Charging stations help label payment'),
      value: t('Charging stations help value payment')
    }
  ]

  const sortStationsByIdAsc = (items: ChargingStation[]): ChargingStation[] =>
    [...items].sort((a, b) => {
      const aId = Number(a.id)
      const bId = Number(b.id)
      if (Number.isNaN(aId) && Number.isNaN(bId)) return 0
      if (Number.isNaN(aId)) return 1
      if (Number.isNaN(bId)) return -1
      return aId - bId
    })

  useEffect(() => {
    ;(() => {
      const mapped =
        data?.map((item) => {
          const totalVolumeValue = item.total_volume_kwh
          const registrationDate =
            item.registration_date ?? format(new Date(), 'yyyy-MM-dd')

          return {
            id: String(item.id),
            merk: item.brand,
            model: item.model ?? '',
            evseId: item.evse_id ?? '',
            totalVolume: toVolumeInputValue(totalVolumeValue),
            registrationDate,
            previousRegistrationDate: item.previous_registration_date ?? null,
            sinceDate: item.since_date,
            midStatus: item.mid_status ?? undefined,
            midSourceText: item.mid_source_text ?? undefined,
            midExplanationText: item.mid_explanation_text ?? undefined,
            autoConnectEnabled: item.auto_connect_enabled
          }
        }) ?? []

      setStations(sortStationsByIdAsc(mapped))

      const sum = mapped.reduce((acc, station) => {
        const value = parseInt(station.totalVolume, 10)
        return acc + (isNaN(value) ? 0 : value)
      }, 0)
      const nextGrandTotal = sum.toString()
      setGrandTotal(nextGrandTotal)
    })()
  }, [data])

  const updateStations = (updated: ChargingStation[]) => {
    setIsFormDirty(true)
    setStations(updated)
  }

  const updateGrandTotal = useCallback((value: string, currentSum?: number) => {
    // If cleared or set to 0, auto-calculate from station volumes
    if (value === '0' || value === '0.00') {
      const auto = (currentSum ?? 0).toString()
      setGrandTotal(auto)
      return
    }
    setGrandTotal(value)
  }, [])

  const getStationVolume = useCallback((s: ChargingStation) => {
    const manual = parseInt(s.totalVolume, 10)
    return isNaN(manual) ? 0 : manual
  }, [])

  const sumOfStationVolumes = useMemo(
    () => stations.reduce((sum, s) => sum + getStationVolume(s), 0),
    [stations, getStationVolume]
  )

  const prevSumRef = useRef(sumOfStationVolumes)

  // Auto-sync grand total when station volumes change
  useEffect(() => {
    if (prevSumRef.current !== sumOfStationVolumes) {
      updateGrandTotal(sumOfStationVolumes.toString(), sumOfStationVolumes)
    }
    prevSumRef.current = sumOfStationVolumes
  }, [sumOfStationVolumes, updateGrandTotal])

  const grandTotalNum = parseFloat(grandTotal)
  const effectiveGrandTotal =
    grandTotal !== '' && !isNaN(grandTotalNum)
      ? grandTotalNum
      : sumOfStationVolumes
  const calculatedEREs = effectiveGrandTotal * ERE_FACTOR

  const addStation = () => {
    if (stations.length >= MAX_STATIONS) {
      setPopupMessage(t('You can add a maximum of 3 charging stations.'))
      setIsShowPopup(true)
      return
    }
    if (stations.length === 0) setIsShowSaveButton(false)
    const newStation = emptyStation()
    updateStations([...stations, newStation])
    setScrollTarget(newStation.id)
  }

  useEffect(() => {
    if (!scrollTarget) return

    const el = document.getElementById(`station-${scrollTarget}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [scrollTarget])

  const [stationToDelete, setStationToDelete] = useState<string | null>(null)

  const removeStation = (id: string) => {
    void (async () => {
      try {
        setSavingStationId(id)

        // Filter out the deleted station locally
        const remainingStations = stations.filter((s) => s.id !== id)

        // We only send saved or valid stations in the payload to sync with DB
        const filteredStations = remainingStations.filter((station) => {
          const isSaved = /^\d+$/.test(station.id)
          const isValid =
            station.merk.trim() !== '' && station.evseId.trim() !== ''
          return isSaved || isValid
        })

        const payload = filteredStations.map((station) => ({
          id: /^\d+$/.test(station.id) ? Number(station.id) : null,
          brand: station.mid?.brand ?? station.merk,
          model: station.mid?.model ?? station.model ?? null,
          evse_id: station.evseId || null,
          total_volume_kwh: Number.parseInt(station.totalVolume || '0', 10),
          registration_date: station.registrationDate,
          mid_id: station.mid?.id ?? station.midId ?? null,
          mid_status: station.mid?.status ?? station.midStatus ?? null,
          mid_source_text:
            station.mid?.source_text ?? station.midSourceText ?? null,
          mid_explanation_text:
            station.mid?.explanation_text ?? station.midExplanationText ?? null
        }))

        const response = await client.put<{ data: ApiChargingStation[] }>(
          '/app-api/me/stations',
          {
            stations: payload
          }
        )

        const mapped = response.data.data.map((item) => ({
          id: String(item.id),
          merk: item.brand,
          model: item.model ?? '',
          evseId: item.evse_id ?? '',
          totalVolume: toVolumeInputValue(item.total_volume_kwh),
          registrationDate: item.registration_date,
          previousRegistrationDate: item.previous_registration_date ?? null,
          sinceDate: item.since_date,
          midId: item.mid_id?.toString(),
          midStatus: item.mid_status ?? undefined,
          midSourceText: item.mid_source_text ?? undefined,
          mid: item.mid ?? null
        }))

        // Keep unsaved stations of remainingStations
        const unsavedStations = remainingStations.filter(
          (s) => !/^\d+$/.test(s.id)
        )

        const combined = [...mapped, ...unsavedStations]
        setStations(sortStationsByIdAsc(combined))

        if (combined.length === 0) {
          setIsShowSaveButton(true)
        }

        setPopupMessage(
          t('The charging station and its data have been removed.')
        )
        setPopupTitle(t('Charging station removed'))
        setIsShowPopup(true)
      } catch {
        setFormError(t('Deletion failed. Please try again.'))
      } finally {
        setSavingStationId(null)
        void queryClient.invalidateQueries({ queryKey: ['customer_stations'] })
      }
    })()
  }

  const handleChange = (
    id: string,
    field: keyof ChargingStation,
    value: string | null
  ) => {
    setFormError('')

    setStations((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s

        const normalizedValue =
          field === 'totalVolume' ? (value ?? '').replace(/\D/g, '') : value

        if (
          field === 'merk' &&
          normalizedValue &&
          normalizedValue.trim() !== ''
        ) {
          setMissingBrandByStation((prevState) => ({
            ...prevState,
            [id]: false
          }))
        }

        return {
          ...s,
          [field]: normalizedValue
        }
      })
    )

    setIsFormDirty(true)
  }

  // Dedicated handler for updating the mid object (and syncing merk/model/midId/midStatus/midSourceText)
  const handleMidChange = (id: string, mid: Mid | null) => {
    setFormError('')
    setIsFormDirty(true)
    setStations((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s
        if (!mid) {
          return {
            ...s,
            mid: null,
            midId: undefined,
            midStatus: undefined,
            midSourceText: undefined,
            midExplanationText: undefined
          }
        }
        if (mid.brand.trim() !== '') {
          setMissingBrandByStation((prevState) => ({
            ...prevState,
            [id]: false
          }))
        }
        return {
          ...s,
          mid,
          merk: mid.brand,
          model: mid.model,
          midId: mid.id,
          midStatus: mid.status,
          midSourceText: mid.source_text ?? undefined,
          midExplanationText: mid.explanation_text ?? undefined
        }
      })
    )
  }

  // When user types freely (not selecting from list), update mid's brand or model text only
  const handleMidFieldText = (
    id: string,
    field: 'brand' | 'model',
    value: string
  ) => {
    setFormError('')
    setIsFormDirty(true)
    if (field === 'brand' && value.trim() !== '') {
      setMissingBrandByStation((prevState) => ({ ...prevState, [id]: false }))
    }
    setStations((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s
        return {
          ...s,
          [field === 'brand' ? 'merk' : 'model']: value,
          mid: s.mid ? { ...s.mid, [field]: value } : null
        }
      })
    )
  }

  const handleSubmit = (
    e?: React.FormEvent | React.MouseEvent,
    targetStationId?: string,
    options?: {
      showPopup?: boolean
      onSuccess?: () => void
      onError?: () => void
    }
  ) => {
    const { showPopup = true, onSuccess, onError } = options ?? {}
    if (e) e.preventDefault()
    setFormError('')

    if (targetStationId) {
      const targetStation = stations.find((s) => s.id === targetStationId)
      if (targetStation) {
        const isBrandMissing = targetStation.merk.trim() === ''
        const isEvseIdMissing = targetStation.evseId.trim() === ''

        setMissingBrandByStation((prev) => ({
          ...prev,
          [targetStationId]: isBrandMissing
        }))
        setMissingEvseIdByStation((prev) => ({
          ...prev,
          [targetStationId]: isEvseIdMissing
        }))

        if (isBrandMissing || isEvseIdMissing) {
          onError?.()
          return
        }
      }
    } else {
      const missingBrands = stations.reduce<Record<string, boolean>>(
        (acc, station) => {
          acc[station.id] = station.merk.trim() === ''
          return acc
        },
        {}
      )
      const hasMissingBrands = Object.values(missingBrands).some(Boolean)

      const missingEvseIds = stations.reduce<Record<string, boolean>>(
        (acc, station) => {
          acc[station.id] = station.evseId.trim() === ''
          return acc
        },
        {}
      )
      const hasMissingEvseIds = Object.values(missingEvseIds).some(Boolean)

      if (hasMissingBrands || hasMissingEvseIds) {
        setMissingBrandByStation(missingBrands)
        setMissingEvseIdByStation(missingEvseIds)
        onError?.()
        return
      }
    }

    void (async () => {
      try {
        setSavingStationId(targetStationId ?? 'global')

        // Build payload for ALL stations:
        // - is_save = true → Saves all stations (or all stations when saving globally)
        // - is_save = false → Only saves (does not update) if numeric IDs are already in the database
        const payload = stations
          .filter((station) => {
            // Only include in the payload if:
            // 1. Target station (to be saved)
            // 2. Already in the DB (numeric ID) → needs to be retained by the backend (not deleted)
            const isTarget = targetStationId
              ? station.id === targetStationId
              : true
            const isSavedInDb = /^\d+$/.test(station.id)
            return isTarget || isSavedInDb
          })
          .map((station) => {
            const isSavedInDb = /^\d+$/.test(station.id)
            const isTarget = targetStationId
              ? station.id === targetStationId
              : true // global save → all is_save = true

            return {
              id: isSavedInDb ? Number(station.id) : null,
              is_save: isTarget,
              brand: station.mid?.brand ?? station.merk,
              model: station.mid?.model ?? station.model ?? null,
              evse_id: station.evseId || null,
              total_volume_kwh: Number.parseInt(station.totalVolume || '0', 10),
              registration_date: station.registrationDate,
              mid_id: station.mid?.id ?? station.midId ?? null,
              mid_status: station.mid?.status ?? station.midStatus ?? null,
              mid_source_text:
                station.mid?.source_text ?? station.midSourceText ?? null,
              mid_explanation_text:
                station.mid?.explanation_text ??
                station.midExplanationText ??
                null
            }
          })

        const response = await client.put<{ data: ApiChargingStation[] }>(
          '/app-api/me/stations',
          {
            stations: payload
          }
        )

        const mapped = response.data.data.map((item) => ({
          id: String(item.id),
          merk: item.brand,
          model: item.model ?? '',
          evseId: item.evse_id ?? '',
          totalVolume: toVolumeInputValue(item.total_volume_kwh),
          registrationDate: item.registration_date,
          previousRegistrationDate: item.previous_registration_date ?? null,
          sinceDate: item.since_date,
          midId: item.mid_id?.toString(),
          midStatus: item.mid_status ?? undefined,
          midSourceText: item.mid_source_text ?? undefined,
          mid: item.mid ?? null
        }))

        // Keep local (unsaved) stations that were not sent in the payload
        const unsavedStations = stations.filter((s) => {
          const isSavedInDb = /^\d+$/.test(s.id)
          const isTarget = targetStationId ? s.id === targetStationId : false
          return !isSavedInDb && !isTarget
        })

        setStations(sortStationsByIdAsc([...mapped, ...unsavedStations]))
        if (showPopup) {
          setPopupMessage(t('The changes have been saved.'))
          setIsShowPopup(true)
        }
        onSuccess?.()
      } catch {
        setFormError(t('Saving failed. Please try again.'))
        onError?.()
      } finally {
        setSavingStationId(null)
        setIsFormDirty(false)
        void queryClient.invalidateQueries({ queryKey: ['customer_stations'] })
      }
    })()
  }

  if (isLoading) {
    return (
      <div className='bg-card flex flex-col items-center justify-center gap-8 rounded-lg p-4 py-24 shadow-sm'>
        <Loader2 className='text-primary size-12 animate-spin' />
        <span>{t('Loading charging stations...')}</span>
      </div>
    )
  }

  if (isError) {
    return (
      <div className='bg-card flex flex-col items-center justify-center gap-8 rounded-lg p-4 py-24 shadow-sm'>
        <XIcon className='text-destructive size-12' />
        <span className='text-destructive'>
          {t('Failed to load charging stations.')}
        </span>
      </div>
    )
  }

  const connectPlatformBaseUrl =
    (import.meta.env.VITE_ZERES_CONNECT_PLATFORM_URL as string | undefined) ||
    'https://connect.zeres.nl'

  const connectPlatformUrl = customer?.connect_id
    ? `${connectPlatformBaseUrl}/?id=${customer.connect_id}`
    : `${connectPlatformBaseUrl}/`

  return (
    <>
      <form
        noValidate
        onSubmit={handleSubmit}
        className='space-y-6'
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        }}
      >
        <div className='mb-8 flex items-center justify-between'>
          <h3 className='text-foreground text-lg font-semibold'>
            {t('My charging stations')}
          </h3>
          {stations.length >= MAX_STATIONS ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type='button'
                    size='sm'
                    className={cn(
                      'gap-2',
                      stations.length >= MAX_STATIONS &&
                        'bg-primary/50 hover:bg-primary/50'
                    )}
                  >
                    <Plus className='h-4 w-4' />
                    {t('Add charging station')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {t(
                      `You can add a maximum of ${MAX_STATIONS} charging stations.`
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button
              type='button'
              onClick={addStation}
              disabled={stations.length >= MAX_STATIONS}
              size='sm'
              className='gap-2'
            >
              <Plus className='h-4 w-4' />
              {t('Add charging station')}
            </Button>
          )}
        </div>

        {stations.length === 0 && (
          <div className='bg-card border-input rounded-lg border p-8 text-center sm:p-12'>
            <Zap className='text-muted-foreground/40 mx-auto mb-4 h-12 w-12' />
            <p className='text-muted-foreground'>
              {t(
                'No charging stations added yet. Click on "+ Add charging station" to start.'
              )}
            </p>
          </div>
        )}
        <TooltipProvider delayDuration={0}>
          {stations.map((station, index) => {
            const explanation =
              station.mid?.explanation_text || station.midExplanationText
            const isStationValid =
              station.merk.trim() !== '' && station.evseId.trim() !== ''

            return (
              <div
                key={station.id}
                id={`station-${station.id}`}
                className='bg-card border-input space-y-4 rounded-lg border p-4 sm:p-6'
              >
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <h4 className='text-foreground font-medium'>
                      {t('Charging station')} {index + 1}
                    </h4>
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => {
                      setStationToDelete(station.id)
                    }}
                  >
                    <Trash2 className='text-destructive h-4 w-4' />
                  </Button>
                </div>

                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                  <div className='space-y-2'>
                    <Label>
                      {t('Brand')}{' '}
                      <span className='text-destructive'>*</span>{' '}
                    </Label>
                    <MIDsAutocomplete
                      searchValue={station.merk}
                      setSearchValue={(value) => {
                        handleMidFieldText(station.id, 'brand', value)
                      }}
                      onSelect={(e) => {
                        if (!e) {
                          handleMidChange(station.id, null)
                          return
                        }
                        handleMidChange(station.id, e)
                      }}
                      onCreated={() => {
                        handleChange(station.id, 'midId', '')
                        handleChange(station.id, 'midStatus', '')
                        handleChange(station.id, 'midSourceText', '')
                        handleChange(station.id, 'midExplanationText', '')
                      }}
                      onClearCreated={() => {}}
                      onPendingChange={() => {}}
                      containerClassName={cn('w-full max-w-auto')}
                      inputClassName={cn(
                        'border-input bg-background ring-offset-background file:text-foreground placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium focus:bg-inherit focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
                        missingBrandByStation[station.id]
                          ? '!border-red-500'
                          : ''
                      )}
                      itemClassName='p-2'
                      displayValue={(mid) => mid.brand}
                      aria-invalid={missingBrandByStation[station.id]}
                      placeholder='Merk'
                      style={
                        missingBrandByStation[station.id]
                          ? { borderColor: 'red' }
                          : undefined
                      }
                      apiUrl='/app-api/mids/autocomplete/brand'
                      fieldName='merk'
                      relatedFieldValue={station.model ?? ''}
                      relatedFieldName='model'
                    />
                    {missingBrandByStation[station.id] && (
                      <p className='text-destructive text-sm'>
                        {t('page_change_password.error.required_field', {
                          field: t('Brand').toLowerCase()
                        })}
                      </p>
                    )}
                  </div>
                  <div className='space-y-2'>
                    <Label>{t('Model')}</Label>
                    <MIDsAutocomplete
                      searchValue={station.model ?? ''}
                      setSearchValue={(value) => {
                        handleMidFieldText(station.id, 'model', value)
                      }}
                      onSelect={(e) => {
                        if (!e) {
                          handleMidChange(station.id, null)
                          return
                        }
                        handleMidChange(station.id, e)
                      }}
                      onCreated={() => {
                        handleChange(station.id, 'midId', '')
                        handleChange(station.id, 'midStatus', '')
                        handleChange(station.id, 'midSourceText', '')
                        handleChange(station.id, 'midExplanationText', '')
                      }}
                      onClearCreated={() => {}}
                      onPendingChange={() => {}}
                      containerClassName='w-full max-w-auto'
                      inputClassName='border-input bg-background ring-offset-background file:text-foreground placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium focus:bg-inherit focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'
                      itemClassName='p-2'
                      displayValue={(mid) => mid.model || ''}
                      aria-invalid={missingBrandByStation[station.id]}
                      placeholder={t('Model')}
                      apiUrl='/app-api/mids/autocomplete/model'
                      fieldName='model'
                      relatedFieldValue={station.merk}
                      relatedFieldName='merk'
                    />
                  </div>
                </div>

                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                  <div className='col-span-1 space-y-2 sm:col-start-1 sm:row-start-1'>
                    <div className='flex items-center gap-1.5'>
                      <Label>{t('MID Meter wel/niet aanwezig')}</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className='text-muted-foreground h-4 w-4 cursor-help' />
                        </TooltipTrigger>
                        <TooltipContent className='max-w-lg'>
                          <p>
                            {explanation
                              ? `${explanation.trim()}${explanation.trim().endsWith('.') ? '' : '.'} Dit veld kunt u niet aanpassen.`
                              : t(
                                  'Dit veld geeft aan of de laadpaal een MID-gecertificeerde meter heeft. Dit veld kunt u niet aanpassen.'
                                )}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      value={t(
                        `status.${station.midStatus ? station.midStatus : 'UNKNOWN'}`
                      )}
                      placeholder='MID Meter aanwezig'
                      readOnly
                      className='cursor-not-allowed'
                    />
                  </div>

                  {/^\d+$/.test(station.id) && (
                    <div className='text-foreground col-span-1 flex flex-col justify-end gap-2 font-medium sm:col-start-2 sm:row-span-2 sm:row-start-1 sm:self-stretch'>
                      {station.evseId.trim() && station.merk.trim() ? (
                        <>
                          <p>
                            {t(
                              'Connect your charger to our platform if not already connected.'
                            )}
                          </p>
                          {(station.autoConnectEnabled ?? true) && (
                            <Button
                              type='button'
                              className='h-auto min-h-10 w-full max-w-full gap-2 px-4 py-2 whitespace-normal shadow-sm'
                              disabled={savingStationId === station.id}
                              onClick={() => {
                                const connectWindow = window.open('', '_blank')

                                handleSubmit(undefined, station.id, {
                                  showPopup: false,
                                  onSuccess: () => {
                                    if (connectWindow) {
                                      connectWindow.location.href =
                                        connectPlatformUrl
                                    }
                                  },
                                  onError: () => {
                                    connectWindow?.close()
                                  }
                                })
                              }}
                            >
                              {savingStationId === station.id ? (
                                <Loader2 className='h-4 w-4 shrink-0 animate-spin' />
                              ) : (
                                <Zap className='h-4 w-4 shrink-0' />
                              )}
                              <span className='leading-snug wrap-break-word'>
                                {savingStationId === station.id
                                  ? t(
                                      'Connect charging station to the Zeres platform...'
                                    )
                                  : t(
                                      'Connect charging station to the Zeres platform'
                                    )}
                              </span>
                            </Button>
                          )}
                        </>
                      ) : (
                        ''
                      )}
                    </div>
                  )}

                  <div className='col-span-1 space-y-2 sm:col-start-1 sm:row-start-2'>
                    <div className='flex items-center gap-1.5'>
                      <Label>{t('Serienummer')}</Label>
                      <span className='text-destructive'>*</span>{' '}
                      <Tooltip
                        open={tooltipOpenId === station.id}
                        onOpenChange={(open) => {
                          setTooltipOpenId(open ? station.id : null)
                        }}
                      >
                        <TooltipTrigger asChild>
                          <Info
                            className='text-muted-foreground h-4 w-4 cursor-help'
                            onClick={() => {
                              setTooltipOpenId(station.id)
                            }}
                          />
                        </TooltipTrigger>
                        <TooltipContent className='max-w-lg'>
                          <p>
                            {t(
                              'See the manual for instructions on finding the serial number.'
                            )}
                          </p>
                          {/* <p className='mt-1'>
                          {t('An example of a serial number is')}:{' '}
                          <strong>NL TMN E 1234</strong>
                        </p> */}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      value={station.evseId}
                      onChange={(e) => {
                        handleChange(station.id, 'evseId', e.target.value)
                        if (e.target.value.trim() !== '') {
                          setMissingEvseIdByStation((prev) => ({
                            ...prev,
                            [station.id]: false
                          }))
                        }
                      }}
                      placeholder='Serienummer'
                      required
                      className={cn(
                        missingEvseIdByStation[station.id]
                          ? '!border-red-500'
                          : ''
                      )}
                    />
                    {missingEvseIdByStation[station.id] && (
                      <p className='text-destructive text-sm'>
                        {t('page_change_password.error.required_field', {
                          field: t('Serienummer').toLowerCase()
                        })}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <p className='text-sm'>
                    {t(
                      'Meer informatie over het vinden van het serienummer van uw laadpaal vindt u in deze'
                    )}{' '}
                    <a
                      href='https://www.zeres.nl/uploads/guides/seriemunner-of-id-van-laadpaal.pdf'
                      target='_blank'
                      className='hover:text-primary text-primary font-bold hover:underline'
                    >
                      {t('document')}.
                    </a>
                  </p>
                  <a></a>
                </div>

                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                  <div className='space-y-2'>
                    <div className='flex items-center gap-1.5'>
                      <Label>{t('Geladen stroom (kWh)')}</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className='text-muted-foreground h-4 w-4 cursor-help' />
                        </TooltipTrigger>
                        <TooltipContent className='max-w-md'>
                          <p>
                            {t(
                              'Enter the total number of kilowatt-hours (kWh) charged for this period.'
                            )}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      type='text'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      value={station.totalVolume}
                      onChange={(e) => {
                        handleChange(station.id, 'totalVolume', e.target.value)
                      }}
                      onWheel={(e) => {
                        e.currentTarget.blur()
                      }}
                      onFocus={(e) => {
                        e.target.select()
                      }}
                      onBlur={() => {
                        if (station.totalVolume.trim() === '') {
                          handleChange(station.id, 'totalVolume', '0')
                        }
                      }}
                      className='bg-accent focus:bg-accent'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label>{t('Registration date')}</Label>
                    <ErrorBoundary
                      fallback={<Skeleton className='h-10 w-full' />}
                      onError={() =>
                        toast.error(
                          t('Something went wrong. Please refresh the page.')
                        )
                      }
                    >
                      <DatePicker
                        value={
                          station.registrationDate
                            ? new Date(station.registrationDate)
                            : undefined
                        }
                        onChange={(date) => {
                          const formatted = date
                            ? format(date, 'yyyy-MM-dd')
                            : null

                          handleChange(
                            station.id,
                            'registrationDate',
                            formatted
                          )
                        }}
                      />
                    </ErrorBoundary>
                  </div>
                </div>
                <div className='flex justify-center pt-2'>
                  <Button
                    type='button'
                    onClick={(e) => {
                      handleSubmit(e, station.id)
                    }}
                    size='lg'
                    className={cn(
                      'gap-2',
                      !isStationValid && 'bg-primary/50 hover:bg-primary/50'
                    )}
                    disabled={savingStationId === station.id || !isStationValid}
                  >
                    <Save className='h-4 w-4' />
                    <span>
                      {savingStationId === station.id
                        ? t('Saving...')
                        : t('Save')}
                    </span>
                  </Button>
                </div>
                <div className='text-muted-foreground mt-4 space-y-3 text-sm'>
                  {chargingStationHelpItems.map((item) => (
                    <p key={item.label} className='leading-6'>
                      <span className='font-bold'>{item.label}</span>{' '}
                      <span>{item.value}</span>
                    </p>
                  ))}
                </div>
              </div>
            )
          })}
        </TooltipProvider>

        {stations.length > 0 && (
          <TooltipProvider delayDuration={0}>
            <div className='bg-card border-input space-y-4 rounded-lg border p-4 sm:p-6'>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-2'>
                  <div className='flex items-center gap-1.5'>
                    <Label>
                      {t('Total volume of all charging stations (kWh)')}
                    </Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className='text-muted-foreground h-4 w-4 cursor-help' />
                      </TooltipTrigger>
                      <TooltipContent className='max-w-md'>
                        <p>{t('Dit veld kunt u niet aanpassen.')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    type='number'
                    min='0'
                    readOnly
                    tabIndex={-1}
                    value={grandTotal}
                    onFocus={(e) => {
                      e.target.blur()
                    }}
                    className='font-semibold'
                  />
                </div>
                <div className='space-y-2'>
                  <div className='flex items-center gap-1.5'>
                    <Label>{t('Calculated number of EREs')}</Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className='text-muted-foreground h-4 w-4 cursor-help' />
                      </TooltipTrigger>
                      <TooltipContent className='max-w-md'>
                        <p>{t('Dit veld kunt u niet aanpassen.')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Input
                    type='number'
                    readOnly
                    tabIndex={-1}
                    onFocus={(e) => {
                      e.target.blur()
                    }}
                    value={Math.floor(calculatedEREs)}
                    className='font-semibold'
                  />
                </div>
              </div>
              {formError !== '' && (
                <p className='text-destructive text-sm'>{formError}</p>
              )}
            </div>
          </TooltipProvider>
        )}

        {isShowSaveButton && (
          <div className='space-y-2 pt-2'>
            {formError !== '' && (
              <p className='text-destructive text-center text-sm'>
                {formError}
              </p>
            )}
            <div className='flex justify-center'>
              <Button
                type='submit'
                size='lg'
                className='gap-2'
                disabled={savingStationId !== null || !isFormDirty}
              >
                <Save className='h-4 w-4' />
                <span>
                  {savingStationId !== null ? 'Opslaan...' : 'Opslaan'}
                </span>
              </Button>
            </div>
          </div>
        )}
      </form>

      <DeleteConfirmDialog
        open={!!stationToDelete}
        onClose={() => {
          setStationToDelete(null)
        }}
        onConfirm={() => {
          if (stationToDelete) removeStation(stationToDelete)
          setStationToDelete(null)
        }}
      />

      <AlertDialog
        open={isShowPopup}
        onOpenChange={() => {
          setIsShowPopup(false)
          setPopupMessage('')
          setPopupTitle(null)
        }}
      >
        <AlertDialogContent overlay={false}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t(popupTitle ?? 'Saved')}</AlertDialogTitle>
            <AlertDialogDescription>{popupMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setIsShowPopup(false)
                setPopupMessage('')
                setPopupTitle(null)
              }}
            >
              {t('Ok')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default ChargingStationsTab
